import {Component, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {Textarea} from 'primeng/textarea';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';

/** What the opener passes in through DynamicDialogConfig.data. */
export interface CancelTaDialogData {
  acceptLabel: string;
  // Which explanation to show, and whether this is the cancel or the reverse path: 'default',
  // 'prepaid' or 'revert'.
  type: string;
  // Both default to the cancellation wording, so the cancel callers pass neither.
  promptLabel?: string;
  placeholder?: string;
}

/**
 * Cancelling a TA needs a reason, which the shared p-confirmdialog cannot collect - it renders a
 * plain message string and its accept button is not reactively disableable. So the cancel flow gets
 * its own dialog, opened by ConfirmationWrapperService.cancelConfirm() - and reversing a
 * cancellation, which the server likewise refuses without a reason, reuses it.
 *
 * Closes with the trimmed reason on accept, and with undefined on Close / Escape / mask dismiss, so
 * the caller can tell a real cancellation from a dismissal.
 */
@Component({
  selector: 'cancel-ta-dialog',
  imports: [
    Button,
    Textarea,
    FormsModule,
  ],
  templateUrl: './cancel-ta-dialog.html',
})
export class CancelTaDialog {
  ref = inject(DynamicDialogRef);
  config = inject<DynamicDialogConfig<CancelTaDialogData>>(DynamicDialogConfig);

  notes = '';

  get type(): string {
    return this.config.data?.type ?? '';
  }

  get acceptLabel(): string {
    return this.config.data?.acceptLabel ?? 'Cancel TA';
  }

  get promptLabel(): string {
    return this.config.data?.promptLabel
      ?? 'Please provide a brief description of why you are cancelling the TA';
  }

  get placeholder(): string {
    return this.config.data?.placeholder
      ?? 'Click here to add a reason for cancelling this Travel Allowance.';
  }

  get canConfirm(): boolean {
    return this.notes.trim().length > 0;
  }

  confirmDialog(): void {
    if (!this.canConfirm) {
      return;
    }
    this.ref.close(this.notes.trim());
  }

  closeDialog(): void {
    this.ref.close(undefined);
  }
}
